/**
 * ¿SE MIRAN SUCURSALES O NEGOCIOS? (2026-10-03)
 *
 * Lo pidió el dueño: «lo que hicimos con todas mis sucursales, también en todos
 * mis negocios». Las pantallas de este apartado sirven para los dos: «Todas las
 * sucursales» (`/sucursales/…`, las de UN negocio) y «Todos mis negocios»
 * (`/grupo/…`, un negocio donde aquí va una sucursal). Lo que cambia entre uno
 * y otro vive aquí:
 *
 *   · `api`    → de dónde salen las cifras (`/sucursales/…` o `/grupo/…`, con
 *                la misma forma: ver `reports/todos_los_negocios.py`);
 *   · `base`   → a dónde llevan los enlaces entre pestañas;
 *   · `clave`  → la caché, para que las cifras de uno no se vean en el otro;
 *   · `u`      → las palabras: «la sucursal» o «el negocio», con su género.
 *
 * ⚠️ Una pantalla nueva del apartado no escribe «sucursal» a mano: usa `u`.
 * Si no, en «Todos mis negocios» diría «sucursal» donde es un negocio.
 */

import { createContext, useContext, type ReactNode } from "react";
import { Info } from "lucide-react";
import { Nota } from "@/components/ui";
import { reportes } from "@/lib/endpoints";
import { useAuth } from "@/stores/auth";

/** Las palabras de cada ámbito, con su género (sucursal es femenino; negocio, masculino). */
export type Palabras = {
  /** sucursal · negocio */
  una: string;
  /** sucursales · negocios */
  unas: string;
  Una: string;
  Unas: string;
  /** la · el */
  art: string;
  Art: string;
  /** las · los */
  arts: string;
  Arts: string;
  /** una · un */
  un: string;
  Un: string;
  /** una · uno («de una en particular») */
  uno: string;
  /** ninguna · ninguno */
  ninguna: string;
  /** todas · todos */
  todas: string;
  Todas: string;
  /** otra · otro */
  otra: string;
  /** otras · otros */
  otras: string;
  /** esta · este */
  esta: string;
  /** de la · del */
  del: string;
  /** a la · al */
  al: string;
  /** en la · en el */
  en: string;
  /** a · o: la terminación de un adjetivo («la más cara», «el más caro») */
  o: string;
  /** as · os */
  os: string;
  /** «Todas las sucursales» · «Todos sus negocios» */
  titulo: string;
  /** local · negocio (donde se cuentan también los almacenes) */
  local: string;
  Local: string;
  /** todos los locales · todos los negocios */
  locales: string;
  Locales: string;
  /** La insignia de la fila «de casa»: Principal · Negocio actual */
  principal: string;
};

const DE_SUCURSALES: Palabras = {
  una: "sucursal",
  unas: "sucursales",
  Una: "Sucursal",
  Unas: "Sucursales",
  art: "la",
  Art: "La",
  arts: "las",
  Arts: "Las",
  un: "una",
  Un: "Una",
  uno: "una",
  ninguna: "ninguna",
  todas: "todas",
  Todas: "Todas",
  otra: "otra",
  otras: "otras",
  esta: "esta",
  del: "de la",
  al: "a la",
  en: "en la",
  o: "a",
  os: "as",
  titulo: "Todas las sucursales",
  local: "local",
  Local: "Local",
  locales: "todos los locales",
  Locales: "Todos los locales",
  principal: "Principal",
};

const DE_NEGOCIOS: Palabras = {
  una: "negocio",
  unas: "negocios",
  Una: "Negocio",
  Unas: "Negocios",
  art: "el",
  Art: "El",
  arts: "los",
  Arts: "Los",
  un: "un",
  Un: "Un",
  uno: "uno",
  ninguna: "ninguno",
  todas: "todos",
  Todas: "Todos",
  otra: "otro",
  otras: "otros",
  esta: "este",
  del: "del",
  al: "al",
  en: "en el",
  o: "o",
  os: "os",
  titulo: "Todos sus negocios",
  local: "negocio",
  Local: "Negocio",
  locales: "todos los negocios",
  Locales: "Todos los negocios",
  principal: "Negocio actual",
};

export type Ambito = {
  /** `true` en «Todos mis negocios». */
  grupo: boolean;
  /** El principio de las direcciones del apartado. */
  base: "/sucursales" | "/grupo";
  /** El principio de las claves de la caché. */
  clave: string;
  api: typeof reportes.todasLasSucursales;
  u: Palabras;
};

const SUCURSALES: Ambito = {
  grupo: false,
  base: "/sucursales",
  clave: "todas-las-sucursales",
  api: reportes.todasLasSucursales,
  u: DE_SUCURSALES,
};

const NEGOCIOS: Ambito = {
  grupo: true,
  base: "/grupo",
  clave: "todos-los-negocios",
  api: reportes.todosLosNegocios,
  u: DE_NEGOCIOS,
};

const Contexto = createContext<Ambito>(SUCURSALES);

/** El ámbito de la pantalla: «Todas las sucursales» salvo dentro de `AmbitoDeNegocios`. */
export function useAmbito(): Ambito {
  return useContext(Contexto);
}

/**
 * Las pantallas de «Todas las sucursales», mirando todos los negocios.
 *
 * Avisa arriba de los negocios que no entran en las cifras: los que cobran en
 * otra moneda (sumar dólares con bolívares no da nada; ver `consolidado.py`).
 */
export function AmbitoDeNegocios({ children }: { children: ReactNode }) {
  const { tenant, tenants } = useAuth();
  const fuera = tenants.filter(
    (t) =>
      t.role === "owner" &&
      t.status !== "deleted" &&
      tenant !== null &&
      t.primary_currency_code !== tenant.primary_currency_code,
  );
  return (
    <Contexto.Provider value={NEGOCIOS}>
      {fuera.length > 0 && (
        <div className="mb-4">
          <Nota tono="aviso" icono={<Info size={15} />}>
            Las cifras de este apartado reúnen los negocios que operan en{" "}
            <strong>{tenant?.primary_currency_code}</strong>. No se incluyen{" "}
            {fuera.map((t) => `${t.name} (${t.primary_currency_code})`).join(", ")}: sumar importes en
            monedas distintas daría un total que varía con el tipo de cambio.
          </Nota>
        </div>
      )}
      {children}
    </Contexto.Provider>
  );
}
